import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ActivateAccountForm } from "../components/auth/ActivateAccountForm";
import { AuthService } from "../services/auth.service.real";
import { ActivateAccountSchemaType } from "../domain/validation/auth.schema";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/Card";
import { CheckCircledIcon } from "@radix-ui/react-icons";

type ActivateStep = "form" | "success" | "invalid-token";

export const ActivateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<ActivateStep>("form");
  const [error, setError] = useState<string>("");
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStep("invalid-token");
      setError("Invalid or missing activation token. Please check your invitation link.");
    }
  }, [token]);

  const activateMutation = useMutation({
    mutationFn: async (data: ActivateAccountSchemaType) => {
      if (!token) {
        throw new Error("Activation token is missing");
      }
      return AuthService.activateAccount(token, data.firstName, data.lastName, data.password);
    },
    onSuccess: () => {
      setError("");
      setStep("success");
      // Redirect to home page after 2 seconds
      setTimeout(() => {
        navigate("/");
      }, 2000);
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to activate account. Please try again.";
      setError(errorMessage);
    },
  });

  const handleActivate = async (data: ActivateAccountSchemaType) => {
    setError("");
    try {
      await activateMutation.mutateAsync(data);
    } catch (err: any) {
      // Error will be handled by onError callback
    }
  };

  const handleDismissError = () => {
    setError("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 font-arial bg-primary-blue">
      <div>
        {step === "form" && (
          <>
            <ErrorMessage message={error} onDismiss={handleDismissError} className="max-w-2xl" />
            <ActivateAccountForm onSubmit={handleActivate} isLoading={activateMutation.isPending} />
          </>
        )}

        {step === "invalid-token" && (
          <Card className="w-full max-w-2xl bg-white shadow-none border border-border-gray rounded-none">
            <CardHeader className="space-y-2 pb-6 pt-8">
              <CardTitle className="text-center text-xl font-bold uppercase tracking-wide text-red-500">
                INVALID ACTIVATION LINK
              </CardTitle>
              <CardDescription className="text-center text-sm text-gray-600">
                {error}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-10 pb-8 flex justify-center">
              <button
                onClick={() => navigate("/login")}
                className="text-link-blue hover:underline text-sm"
              >
                Return to Login
              </button>
            </CardContent>
          </Card>
        )}

        {step === "success" && (
          <Card className="w-full max-w-2xl bg-white shadow-none border border-border-gray rounded-none">
            <CardHeader className="space-y-2 pb-6 pt-8">
              <div className="flex justify-center mb-4">
                <CheckCircledIcon className="h-16 w-16 text-green-500" />
              </div>
              <CardTitle className="text-center text-xl font-bold uppercase tracking-wide text-green-600">
                ACCOUNT ACTIVATED SUCCESSFULLY
              </CardTitle>
              <CardDescription className="text-center text-sm text-gray-600">
                Your account has been activated and you have been logged in. Redirecting to your
                home page...
              </CardDescription>
            </CardHeader>
          </Card>
        )}
      </div>
    </div>
  );
};
